import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Heading from '@amzn/meridian/heading';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import DataSource from '../../../api';
import { StateContext } from '../../../GlobalStateStore';
import { EmptyProfile, Profile } from '../../../Types';
import DataState from '../../../Types/DataState';
import { getCurrentWeek, renderUrl, urls } from '../../../Util';
import { RecommendedTab } from './Tab/RecommendedTab';
import { RecentTab } from './Tab/RecentTab';
import {
  ConsolidatedContact,
  ContactStatusOptionsObject,
  ContactViewTabObject,
  RecommendedContactStatusOptionsObject,
} from '../../../api/CoachingNotes/schema';
import ReviewedTab from './Tab/ReviewedTab';
import Button from '@amzn/meridian/button';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PerformancePortalContext from '../../PerformancePortalContext';
import SavedTab from './Tab/SavedTab';

interface ConsolidatedContactsProps {
  userId: string | undefined;
  week: string | null;
  sourcePage?: string;
}

const ConsolidatedContacts: React.FC<ConsolidatedContactsProps> = ({
  userId,
  week,
  sourcePage,
}: ConsolidatedContactsProps): React.ReactElement => {
  const apiNaws = DataSource.getDataFeed('NAWS');
  const { clearPerformancePortalContextValue } = useContext(
    PerformancePortalContext
  );

  const [profile, setProfile] = useState<Profile>(EmptyProfile);
  const [profileState, setProfileState] = useState(DataState.LOADING);

  const [contactList, setContactList] = useState<ConsolidatedContact[]>([]);
  const [contactListState, setContactListState] = useState(DataState.LOADING);

  const [recommendedList, setRecommendedList] = useState<ConsolidatedContact[]>(
    []
  );
  const [recentList, setRecentList] = useState<ConsolidatedContact[]>([]);
  const [savedList, setSavedList] = useState<ConsolidatedContact[]>([]);

  const [reviewedList, setReviewedList] = useState<ConsolidatedContact[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const contactReviewId = searchParams.get('contactReviewId');

  const hashTab =
    location.hash &&
    Object.keys(ContactViewTabObject).includes(location.hash.substring(1)) &&
    location.hash.substring(1);

  const tabInference = contactReviewId
    ? ContactViewTabObject.REVIEWED
    : hashTab || ContactViewTabObject.RECOMMENDED;

  const [currentTab, setCurrentTab] = useState(tabInference);

  // GGGG refers to the year of the week. This may be different from YYYY
  // When part of the week is from 2024 and part of it is from 2025. e.g. 2024-12-30 (Monday)
  // In such cases, GGGG refers to the new year (e..g 2025) and YYYY refers to the previous year (e.g. 2024).
  const startWeekParam = searchParams.get('startweek');
  const endWeekParam = searchParams.get('endweek');

  const weeksToSubtract = 4;

  const startWeek =
    startWeekParam ?? moment().subtract(weeksToSubtract, 'w').format('GGGG-WW');
  const endWeek = endWeekParam ?? moment().format('GGGG-WW');

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { cscEntityPrefix, isTm } = inspireOptions.data;
  const userIdWithPrefix =
    userId && cscEntityPrefix ? `${cscEntityPrefix}:${userId}` : '';

  const [tabTrackingValue, setTabTrackingValue] = useState('Recommended');

  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED && userId) {
      const queryVariables = apiNaws.prepareQuery(
        week || getCurrentWeek(),
        userId,
        cscEntityPrefix
      );

      apiNaws.getProfileData(queryVariables, setProfile, setProfileState);
    }
  }, [inspireOptions.state, userId]);

  const refreshContacts = useCallback(() => {
    const { userName } = profile.user;
    if (userName !== '') {
      const userID = userId ? `${cscEntityPrefix}:${userId}` : '';
      const queryVariablesNaws = apiNaws.prepareQueryforRecommendationsGetter(
        userID,
        userName,
        startWeek,
        endWeek
      );
      apiNaws.getConsolidatedContacts(
        queryVariablesNaws,
        setContactList,
        setContactListState
      );
    }
  }, [profile]);

  useEffect(() => {
    if (currentTab === ContactViewTabObject.RECOMMENDED) {
      setTabTrackingValue('Recommended');
    } else if (currentTab === ContactViewTabObject.RECENT) {
      setTabTrackingValue('Recent');
    } else if (currentTab === ContactViewTabObject.SAVED) {
      setTabTrackingValue('Saved');
    } else if (currentTab === ContactViewTabObject.REVIEWED) {
      setTabTrackingValue('Reviewed');
    }
  }, [currentTab]);

  useEffect(() => {
    refreshContacts();
  }, [profile]);

  useEffect(() => {
    const contacts = contactList;

    setRecommendedList(
      contacts.filter((c: { recommended: number }) => c.recommended !== 0)
    );
    setRecentList(contacts.filter((c: { recent: number }) => c.recent !== 0));
    setSavedList(
      contacts.filter(
        (c: any) =>
          c.saved !== 0 &&
          (c.contactStatus === ContactStatusOptionsObject.DRAFT ||
            c.contactStatus === null) &&
          c.status === RecommendedContactStatusOptionsObject.REVIEWED
      )
    );
    setReviewedList(
      contacts.filter(
        (c: any) =>
          c.contactStatus === ContactStatusOptionsObject.REVIEWED ||
          c.contactStatus === ContactStatusOptionsObject.DELIVERED ||
          c.status === RecommendedContactStatusOptionsObject.PENDINGCOACHING // Bandaid Fix - To Cover legacy form bump to form library
      )
    );
  }, [contactList, contactListState]);

  //Sync state for scrolling functionality
  useEffect(() => {
    if (contactListState === DataState.LOADED && contactReviewId) {
      const reviewedTab = document.querySelector(
        '[data-cy="contact-table-reviewed-tab"]'
      );
      if (reviewedTab) {
        reviewedTab.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });

        setTimeout(() => {
          window.scrollBy({
            top: 500,
            behavior: 'smooth',
          });
        }, 5);
      }
    }
  }, [contactListState, contactReviewId]);

  if (profileState === DataState.LOADING) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Loader type='circular' size='large' />
      </div>
    );
  }

  if (profileState === DataState.ERROR) {
    return (
      <Alert type='error'>
        <FormattedMessage
          id='cs_perfportal_error_reason_1'
          defaultMessage='Your credentials may be incorrect or inadequate.'
        />
      </Alert>
    );
  }
  const isNotReviewedExist = (contactList: ConsolidatedContact[]) => {
    if (
      contactList.some(
        contact =>
          contact.status === RecommendedContactStatusOptionsObject.NOTREVIEWED
      )
    )
      return true;
    else return false;
  };

  return (
    <Box>
      <Row widths={['grid-10', 'grid-2']}>
        <Heading level={1} type='h400'>
          <span id='nav-recommended' />
          <FormattedMessage
            id='cs_perfportal_contact_table_heading_formatted'
            defaultMessage='Contacts for coaching'
          />
        </Heading>
        <Button
          data-csa-c-type='button'
          data-csa-c-slot-id='contact-table-new-contact-review-button'
          data-csa-c-content-id='coaching-notes-history-tm-new-contact-review-button'
          data-csa-c-tag1='Team'
          data-csa-c-tag2='Contact Table'
          data-csa-c-tag3={tabTrackingValue}
          data-cy='contact-table-new-contact-review-button'
          href={renderUrl(`${urls.contactReview}/${userId}`)}
          onClick={href => {
            // context is also cleared on top level nav change in PerformancePortalLandingProps
            // theoretically, this will not do anything, but included here as a backstop incase the user ends up on this page via an alternate path
            clearPerformancePortalContextValue();
            navigate(href);
          }}
          type='tertiary'
          disabled={
            (contactListState !== DataState.LOADED &&
              contactList.length === 0) ||
            !isNotReviewedExist(contactList)
          }
        >
          <FormattedMessage
            id='cs_perfportal_new_contact_review'
            defaultMessage='New contact review'
          />
        </Button>
      </Row>
      {contactListState === DataState.LOADING && (
        <div style={{ textAlign: 'center' }}>
          <Loader type='circular' size='large' />
        </div>
      )}
      {contactListState === DataState.ERROR && (
        <Alert type='error'>
          <FormattedMessage
            id='cs_perfportal_error_reason_1'
            defaultMessage='Your credentials may be incorrect or inadequate.'
          />
        </Alert>
      )}
      {contactListState === DataState.LOADED && (
        <>
          <Row
            spacingInset='400 none 300'
            spacing='none'
            data-cy='contact-table-tab-group-row'
          >
            <TabGroup value={currentTab} onChange={setCurrentTab}>
              <Tab
                value={ContactViewTabObject.RECOMMENDED}
                data-csa-c-type='tab'
                data-csa-c-slot-id='contact-table-recommended-tab-slot'
                data-csa-c-content-id='contact-table-recommended-tab'
                data-csa-c-tag1='Team'
                data-csa-c-tag2='Contact Table'
                data-csa-c-tag3='Recommended'
                data-cy='contact-table-recommended-tab'
              >
                <FormattedMessage
                  id='cs_perfportal_contact_table_tabname_recommended'
                  defaultMessage='Recommended'
                />
              </Tab>
              <Tab
                value={ContactViewTabObject.RECENT}
                data-csa-c-type='tab'
                data-csa-c-slot-id='contact-table-recent-tab-slot'
                data-csa-c-content-id='contact-table-recent-tab'
                data-csa-c-tag1='Team'
                data-csa-c-tag2='Contact Table'
                data-csa-c-tag3='Recent'
                data-cy='contact-table-recent-tab'
              >
                <FormattedMessage
                  id='cs_perfportal_contact_table_tabname_recent'
                  defaultMessage='Recent'
                />
              </Tab>
              <Tab
                value={ContactViewTabObject.SAVED}
                data-csa-c-type='tab'
                data-csa-c-slot-id='contact-table-save-tab-slot'
                data-csa-c-content-id='contact-table-save-tab'
                data-csa-c-tag1='Team'
                data-csa-c-tag2='Contact Table'
                data-csa-c-tag3='Saved'
                data-cy='contact-table-saved-tab'
              >
                <FormattedMessage
                  id='cs_perfportal_contact_table_tabname_saved'
                  defaultMessage='Saved'
                />
              </Tab>
              <Tab
                value={ContactViewTabObject.REVIEWED}
                data-csa-c-type='tab'
                data-csa-c-slot-id='contact-table-reviewed-tab-slot'
                data-csa-c-content-id='contact-table-reviewed-tab'
                data-csa-c-tag1='Team'
                data-csa-c-tag2='Contact Table'
                data-csa-c-tag3='Reviewed'
                data-cy='contact-table-reviewed-tab'
              >
                <FormattedMessage
                  id='cs_perfportal_contact_table_tabname_reviewed'
                  defaultMessage='Reviewed'
                />
              </Tab>
            </TabGroup>
          </Row>
          <Row>
            {currentTab === ContactViewTabObject.RECOMMENDED && (
              <RecommendedTab
                contacts={recommendedList}
                username={profile.user.userName}
                userID={userIdWithPrefix}
                sourcePage={sourcePage}
              />
            )}
            {currentTab === ContactViewTabObject.RECENT && (
              <RecentTab
                contacts={recentList}
                username={profile.user.userName}
                userID={userIdWithPrefix}
                sourcePage={sourcePage}
              />
            )}
            {currentTab === ContactViewTabObject.SAVED && (
              <SavedTab
                contacts={savedList}
                username={profile.user.userName}
                userID={userIdWithPrefix}
                sourcePage={sourcePage}
              />
            )}
            {currentTab === ContactViewTabObject.REVIEWED && (
              <ReviewedTab
                contacts={reviewedList}
                username={profile.user.userName}
                userID={userIdWithPrefix}
                refreshContacts={refreshContacts}
                sourcePage={sourcePage}
                isTm={isTm}
              />
            )}
          </Row>
        </>
      )}
    </Box>
  );
};
export default ConsolidatedContacts;
